import styles from './testCaseRegularMenu.module.scss';
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { useTranslation } from "react-i18next";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import { TestCaseTemplateModal } from "../testCaseTemplateModal/testCaseTemplateModal";
import { TransformExecuteResponse } from "../../types/clientTypes";
import { ReactComponent as AddIcon } from "../../shared/images/add.svg";
import { ReactComponent as AddFolderIcon } from "../../shared/images/addFolder.svg";
import {
  deleteExecuteResponse,
  deleteSelectedTestCase,
  deleteSelectedTestCaseTemplate,
  setExecuteResponse,
  setSelectedTestCase,
  setSelectedTestCaseTemplate
} from "../../store/slices/testCaseTemplateSlice";
import { setShowSpinner } from "../../store/slices/mainSlice";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import {
  TestCase,
  TestCaseTemplate
} from "../../types/testCaseTemplateTypes";
import { FolderModal } from "../folderModal/folderModal";
import { useGetAllFolderQuery } from "../../api/testCaseTemplateFolderService";
import {
  Folder,
  FolderWithTemplate
} from "../../types/testCaseTemplateFolderTypes";
import { Accordion } from "../../shared/components/accordion/accordion";
import { TestCaseTemplates } from "./testCaseTemplates/testCaseTemplates";
import { FolderTestCaseTemplates } from "./folderTestCaseTemplates/folderTestCaseTemplates";
import {
  useLazyGetAllFolderTemplatesForSearchQuery,
  useLazyGetAllFolderTemplatesQuery
} from "../../api/testCaseTemplateService";
import spinnerGif from "../../shared/images/spinner.gif";
import { RunTestCasesButton } from "../runTestCasesButton/runTestCasesButton";
import { getTransformTestCase } from "../../utils/testCaseUtils";

export const TestCaseRegularMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);
  const selectedTestCaseTemplate = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCaseTemplate);

  const [showTestCaseTemplateModal, setShowTestCaseTemplateModal] = useState<boolean>(false);
  const [showFolderModal, setShowFolderModal] = useState<boolean>(false);
  const [editTestCaseTemplate, setEditTestCaseTemplate] = useState<TestCaseTemplate>();
  const [editFolder, setEditFolder] = useState<Folder>();
  const [searchValue, setSearchValue] = useState<string>("");

  const [foldersWithTemplates, setFoldersWithTemplates] = useState<Array<FolderWithTemplate>>([]);
  const [filteredFolders, setFilteredFolders] = useState<Array<FolderWithTemplate>>([]);
  const [isLoadingFilteredFolders, setIsLoadingFilteredFolders] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>()
  const [needFindTemplate, setNeedFindTemplate] = useState(false);

  const { data: folders, refetch: refetchFolders, isLoading: isLoadingFolders, isFetching: isFetchingFolders } = useGetAllFolderQuery("", { refetchOnMountOrArgChange: true });
  const [getAllFoldersForSearch, { data: foldersForSearch, isLoading: isLoadingFoldersForSearch, isFetching: isFetchingFoldersForSearch}] = useLazyGetAllFolderTemplatesForSearchQuery();
  const [getAllFolderTemplates, { data: folderTemplates, isFetching: isFetchingFolderTemplates }] = useLazyGetAllFolderTemplatesQuery();

  useEffect(() => {
    if (!selectedTestCaseTemplate.id && !isLoadingFilteredFolders) {
      if (activeIndex != null) {
        if (filteredFolders[activeIndex].testCaseTemplates.length === 0) {
          setActiveIndex(undefined);
        }
      }
    }
  }, [selectedTestCaseTemplate, isLoadingFilteredFolders, setActiveIndex]);

  useEffect(() => {
    if (isLoadingFolders || isFetchingFolders) {
      setIsLoadingFilteredFolders(true);
    }
  }, [isLoadingFolders, isFetchingFolders, setIsLoadingFilteredFolders]);

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingFolders || isFetchingFolders));
  }, [isLoadingFolders, isFetchingFolders, dispatch]);

  const setFolderWithTemplatesAfterFetchingFolders = useCallback(() => {
    if (!!folders) {
      setFoldersWithTemplates((foldersWithTemplates) => folders.map(folder => {
        const findItem = foldersWithTemplates.find(item => item.id === folder.id);
        let templates: Array<TestCaseTemplate> = [];
        if (!!findItem) {
          if (findItem.isFetchingTemplate) {
            templates = findItem.testCaseTemplates;
          }
        }
        return {
          id: folder.id,
          name: folder.name,
          isFetchingTemplate: false,
          templatesCount: folder.templatesCount,
          testCaseTemplates: templates,
          isNotDelete: folder.isNotDelete
        };
      }))
      getAllFoldersForSearch("");
    }
  }, [folders, setFoldersWithTemplates, getAllFoldersForSearch]);

  useEffect(() => {
    if (!isFetchingFolders) {
      setFolderWithTemplatesAfterFetchingFolders();
    }
  }, [isFetchingFolders, setFolderWithTemplatesAfterFetchingFolders]);

  useEffect(() => {
    if (!!foldersForSearch && !isLoadingFoldersForSearch && !isFetchingFoldersForSearch && !isLoadingFolders && !isFetchingFolders) {
      let localNeedFindTemplate = needFindTemplate;
      setFoldersWithTemplates((foldersWithTemplates) => foldersForSearch.map(folder => {
        const findItem = foldersWithTemplates.find(item => item.id === folder.id);
        let testCaseTemplates: Array<TestCaseTemplate> = folder.testCaseTemplates.map(template => ({
          id: template.id,
          name: template.name,
          testCaseTemplateFolderId: template.testCaseTemplateFolderId,
          isPositive: template.isPositive,
          methodId: 0,
          priorityId: 0,
          statusCodeId: 0,
          maximumExecutionTimeout: 0,
          requestHeaders: [],
          environmentServers: [],
          requestParameters: [],
          responseParameters: [],
          isJsonSchemaValidation: false,
          childTestCases: template.childTestCases.map(testCase => ({
            id: testCase.id,
            name: testCase.name,
            testCaseTemplateId: testCase.testCaseTemplateId,
            requestParameterValues: [],
            responseParameterValues: []
          }))
        }));
        let isFetchingTemplate = false;
        if (!!findItem) {
          if (localNeedFindTemplate) {
            const findTemplate = folder.testCaseTemplates.find(template => template.id === selectedTestCaseTemplate.id);
            if (!!findTemplate) {
              localNeedFindTemplate = false;
            }
          } else if (findItem.isFetchingTemplate) {
            isFetchingTemplate = true;
            testCaseTemplates = findItem.testCaseTemplates;
          }
        }
        return {
          id: folder.id,
          name: folder.name,
          isFetchingTemplate: isFetchingTemplate,
          templatesCount: folder.templatesCount,
          isNotDelete: folder.isNotDelete,
          testCaseTemplates
        }
      }));
    }
  }, [foldersForSearch, needFindTemplate, isLoadingFoldersForSearch, isFetchingFoldersForSearch, isLoadingFolders, isFetchingFolders, setFoldersWithTemplates]);

  useEffect(() => {
    if (needFindTemplate && !isLoadingFilteredFolders) {
      const indexFolder = filteredFolders.findIndex(folder => {
        return !!folder.testCaseTemplates.find(template => template.id === selectedTestCaseTemplate.id);
      });

      if (indexFolder > -1) {
        const folder = filteredFolders[indexFolder];
        setActiveIndex(indexFolder);
        getAllFolderTemplates(folder.id as number);
      }
      setNeedFindTemplate(false);
    }
  }, [selectedTestCaseTemplate, needFindTemplate, filteredFolders, isLoadingFilteredFolders, getAllFolderTemplates, setActiveIndex, setNeedFindTemplate]);

  useEffect(() => {
    if ((!!folderTemplates && !isFetchingFolderTemplates) && activeIndex != null) {
      const newFoldersWithTemplate = [...foldersWithTemplates];
      const findFolder = newFoldersWithTemplate.find(item => item.id === filteredFolders[activeIndex].id);
      if (!!findFolder) {
        findFolder.testCaseTemplates = [...folderTemplates];
        findFolder.isFetchingTemplate = true;
        setFoldersWithTemplates(newFoldersWithTemplate);
      }
    }
  }, [folderTemplates, isFetchingFolderTemplates, setFoldersWithTemplates]);

  const getFilteredFolders = useCallback((): Array<FolderWithTemplate> => {
    const newFolders: Array<FolderWithTemplate> = [];
    foldersWithTemplates.forEach(folder => {
      if (folder.name.toLowerCase().includes(searchValue.toLowerCase())) {
        newFolders.push(folder);
      } else {
        const newTemplates: Array<TestCaseTemplate> = [];
        folder.testCaseTemplates.forEach(template => {
          if (template.name.toLowerCase().includes(searchValue.toLowerCase())) {
            newTemplates.push(template);
          } else {
            const newTestCases: Array<TestCase> = [];
            template.childTestCases.forEach(testCase => {
              if (testCase.name.toLowerCase().includes(searchValue.toLowerCase())) {
                newTestCases.push(testCase);
              }
            });
            if (newTestCases.length !== 0) {
              const newTemplate: TestCaseTemplate = {
                ...template,
                childTestCases: [...newTestCases]
              };
              newTemplates.push(newTemplate);
            }
          }
        });
        if (newTemplates.length !== 0) {
          const newFolder: FolderWithTemplate = {
            ...folder,
            testCaseTemplates: [...newTemplates]
          };
          newFolders.push(newFolder);
        }
      }
    });
    return newFolders;
  }, [foldersWithTemplates, searchValue]);

  useEffect(() => {
    let activeFolder: FolderWithTemplate | undefined;
    if (activeIndex != null) {
      activeFolder = filteredFolders[activeIndex];
    }
    const newFilteredFolders = getFilteredFolders();
    setFilteredFolders(newFilteredFolders);
    if (!isLoadingFoldersForSearch && !isFetchingFoldersForSearch) {
      setIsLoadingFilteredFolders(false);
    }
    if (!!activeFolder) {
      const newActiveIndex = newFilteredFolders.findIndex(item => item.id === activeFolder?.id);
      if (newActiveIndex > -1) {
        setActiveIndex(newActiveIndex);
        if (!newFilteredFolders[newActiveIndex].isFetchingTemplate) {
          getAllFolderTemplates(newFilteredFolders[newActiveIndex].id as number);
        }
      } else if (newFilteredFolders.length === 1 && newFilteredFolders[0].templatesCount !== 0) {
        setActiveIndex(0);
        getAllFolderTemplates(newFilteredFolders[0].id as number);
      } else {
        setActiveIndex(undefined);
      }
    } else if (newFilteredFolders.length === 1 && newFilteredFolders[0].templatesCount !== 0) {
      setActiveIndex(0);
      getAllFolderTemplates(newFilteredFolders[0].id as number);
    }

  }, [getFilteredFolders, getAllFolderTemplates, setActiveIndex, setFilteredFolders, setIsLoadingFilteredFolders]);

  useEffect(() => {
    dispatch(deleteExecuteResponse());
  }, [selectedTestCase.id, selectedTestCaseTemplate, dispatch]);

  const closeTestCaseTemplateModal = useCallback((): void => {
    setShowTestCaseTemplateModal(false);
    setEditTestCaseTemplate(undefined);
  }, [setShowTestCaseTemplateModal, setEditTestCaseTemplate]);

  const afterCreateTestCaseTemplate = useCallback((createId: number): void => {
    closeTestCaseTemplateModal();
    const newSelectedTestCaseTemplate = {...selectedTestCaseTemplate};
    newSelectedTestCaseTemplate.id = createId;
    dispatch(setSelectedTestCaseTemplate(newSelectedTestCaseTemplate));
    dispatch(deleteSelectedTestCase());
    setNeedFindTemplate(true);
    refetchFolders();
  }, [selectedTestCaseTemplate, closeTestCaseTemplateModal, refetchFolders, setNeedFindTemplate, dispatch])

  const closeFolderModal = useCallback((isSave: boolean): void => {
    setShowFolderModal(false);
    setEditFolder(undefined);
    if (isSave) {
      setActiveIndex(undefined);
      refetchFolders();
    }
  }, [setShowFolderModal, setEditFolder, setActiveIndex, refetchFolders])

  const transformTestCase = useCallback((): void => {
    const newSelectedTest = getTransformTestCase(selectedVariables, selectedTestCaseTemplate, selectedTestCase);
    dispatch(setSelectedTestCase({ testCase: newSelectedTest, template: selectedTestCaseTemplate }));
  }, [selectedVariables, selectedTestCaseTemplate, selectedTestCase, dispatch])

  const addFolderTemplateClick = useCallback((folder: Folder) => {
    setEditTestCaseTemplate({
      name: `${folder.name} - folder`,
      testCaseTemplateFolderId: folder.id as number,
      isPositive: false,
      priorityId: 0,
      childTestCases: [],
      requestHeaders: [],
      responseParameters: [],
      requestParameters: [],
      maximumExecutionTimeout: 0,
      methodId: 0,
      statusCodeId: 0,
      environmentServers: [],
      isJsonSchemaValidation: false
    });
    setShowTestCaseTemplateModal(true);
  }, [setEditTestCaseTemplate, setShowTestCaseTemplateModal]);

  const editFolderClick = useCallback((folder: Folder) => {
    setEditFolder(folder);
    setShowFolderModal(true);
  }, [setEditFolder, setShowFolderModal]);

  const changeActiveFolders = useCallback((folderIndex: number): void => {
    const folder = filteredFolders[folderIndex];
    if (folder.templatesCount !== 0) {
      if (activeIndex === folderIndex) {
        setActiveIndex(undefined);
      } else {
        setActiveIndex(folderIndex);
        getAllFolderTemplates(folder.id as number);
      }
      dispatch(deleteSelectedTestCaseTemplate());
    }
  }, [filteredFolders, activeIndex, setActiveIndex, getAllFolderTemplates, dispatch]);

  const updateExecuteResponse = useCallback((executeResponse: TransformExecuteResponse) => {
    dispatch(setExecuteResponse(executeResponse));
  }, [dispatch])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          <div className={styles.header}>
            <div className={styles.actions}>
              {!!folders?.length && (
                <div className={styles.addTestCase} onClick={() => setShowTestCaseTemplateModal(true)}>
                  <AddIcon/>
                  {t("testCaseTemplate.addTemplate")}
                </div>
              )}
              <div className={styles.addFolder} onClick={() => setShowFolderModal(true)}>
                <AddFolderIcon/>
                {t("testCaseTemplate.addFolder")}
              </div>
            </div>
            <div className={styles.search}>
              <SearchInput onChange={setSearchValue}
                           value={searchValue}
                           placeholder={!!foldersForSearch ? t("common.search") : t("common.searchIsLoading") }
                           disabled={!foldersForSearch}/>
            </div>
          </div>

          <div className={styles.folders}>
            <Accordion activeClassName={styles.active}
                       maxHeight={400}
                       activeIndex={activeIndex}
                       customClick={true}
                       data={ filteredFolders.map((folder, indexFolder) => {
                         return {
                           title: (
                             <FolderTestCaseTemplates key={indexFolder}
                                                      folder={folder}
                                                      refetch={refetchFolders}
                                                      onClick={() => changeActiveFolders(indexFolder)}
                                                      arrowClass={styles.arrow}
                                                      onAddTemplateClick={() => addFolderTemplateClick(folder)}
                                                      onEditClick={() => editFolderClick(folder)}/>
                           ),
                           child: (!folder.testCaseTemplates?.length
                               ? (
                                 <div className={styles.spinner}>
                                   <img src={spinnerGif} alt=""/>
                                 </div>
                               )
                               : <TestCaseTemplates testCaseTemplates={folder.testCaseTemplates}
                                                    needFindTemplate={() => setNeedFindTemplate(true)}
                                                    isActive={indexFolder === activeIndex}
                                                    refetch={refetchFolders}/>
                           ),
                           disabled: folder.templatesCount === 0
                         }
                       })}/>
          </div>
        </div>

        <div className={styles.bottomContainer}>
          <RunTestCasesButton selectedTestCases={[selectedTestCase]}
                              beforeRunIfWasLaunched={transformTestCase}
                              testCaseTemplates={[selectedTestCaseTemplate]}
                              updateExecuteResponse={updateExecuteResponse}
                              disabledRunButton={!selectedTestCase.id}/>
        </div>
      </div>
      {showTestCaseTemplateModal && (<TestCaseTemplateModal testCaseTemplate={editTestCaseTemplate}
                                                            onCreate={afterCreateTestCaseTemplate}
                                                            onUpdate={closeTestCaseTemplateModal}
                                                            onDelete={closeTestCaseTemplateModal}
                                                            onClose={closeTestCaseTemplateModal}/>)}
      {showFolderModal && (<FolderModal onClose={closeFolderModal} folder={editFolder}/>)}
    </>
  )
}